import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/ui/combobox';
import { LocationCombobox } from '@/components/shared/LocationCombobox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useClients } from '@/hooks/useClients';
import { useLocations } from '@/hooks/useLocations';
import { useManagers } from '@/hooks/useManagers';
import { useBillingAddresses } from '@/hooks/useBillingAddresses';
import { useUpdateJob } from '@/hooks/useJobs';
import { formatClientName, formatManagerName } from '@jobos/shared';
import type { Location } from '@jobos/shared/types/location';

interface ChangeClientModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: string;
  currentClientId: string;
}

export function ChangeClientModal({
  open,
  onOpenChange,
  jobId,
  currentClientId,
}: ChangeClientModalProps) {
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [selectedManagerId, setSelectedManagerId] = useState('__none__');
  const [selectedBillingAddressId, setSelectedBillingAddressId] = useState('__none__');
  const [isSaving, setIsSaving] = useState(false);
  const { mutateAsync: updateJob } = useUpdateJob(jobId, { showToast: false });

  const { data: clients = [] } = useClients();
  const { data: locations = [] } = useLocations(selectedClientId || undefined);
  const { data: managers = [] } = useManagers(selectedClientId || undefined);
  const { data: billingAddresses = [] } = useBillingAddresses(selectedClientId || undefined);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setSelectedClientId('');
      setSelectedLocationId('');
      setSelectedManagerId('__none__');
      setSelectedBillingAddressId('__none__');
    }
  }, [open]);

  // When client changes, reset dependent fields and auto-set defaults
  useEffect(() => {
    if (!selectedClientId) return;
    setSelectedLocationId('');
    setSelectedManagerId('__none__');
    // Auto-select default billing address
    const defaultAddr = billingAddresses.find((a) => a.isDefault);
    setSelectedBillingAddressId(defaultAddr ? defaultAddr.id : '__none__');
  }, [selectedClientId, billingAddresses]);

  // Auto-fill manager from selected location
  useEffect(() => {
    if (!selectedLocationId || locations.length === 0) return;
    const loc = locations.find((l) => l.id === selectedLocationId);
    if (loc?.managerId) {
      setSelectedManagerId(loc.managerId);
    }
  }, [selectedLocationId, locations]);

  const clientOptions = clients
    .filter((c) => c.id !== currentClientId)
    .map((c) => ({
      value: c.id,
      label: formatClientName(c.name, c.firstName, c.lastName),
    }));

  const managerOptions = [
    { value: '__none__', label: 'No Manager' },
    ...managers.map((m) => ({
      value: m.id,
      label: formatManagerName(m.name, m.firstName, m.lastName),
    })),
  ];

  const handleLocationCreated = (location: Location) => {
    setSelectedLocationId(location.id);
  };

  const handleConfirm = async () => {
    if (!selectedClientId || !selectedLocationId) return;

    setIsSaving(true);
    try {
      const billingAddr = billingAddresses.find((a) => a.id === selectedBillingAddressId);

      // Single atomic API call — avoids FK issues from split debounced updates
      await updateJob({
        clientId: selectedClientId,
        locationId: selectedLocationId,
        managerId: selectedManagerId === '__none__' ? null : selectedManagerId,
        billingAddressLabel: billingAddr?.label || undefined,
        billingAddressLine1: billingAddr?.addressLine1 || undefined,
        billingAddressLine2: billingAddr?.addressLine2 || undefined,
        billingCity: billingAddr?.city || undefined,
        billingState: billingAddr?.state || undefined,
        billingZip: billingAddr?.zip || undefined,
      });

      onOpenChange(false);
    } finally {
      setIsSaving(false);
    }
  };

  const canConfirm = selectedClientId && selectedLocationId && !isSaving;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" mobileFullscreen>
        <DialogHeader>
          <DialogTitle>Change Client</DialogTitle>
          <DialogDescription>
            Changing the client will also update the service address, manager, and billing address.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Client
            </label>
            <Combobox
              value={selectedClientId}
              onValueChange={setSelectedClientId}
              options={clientOptions}
              placeholder="Select a client..."
              searchPlaceholder="Search clients..."
              emptyText="No clients found."
            />
          </div>

          {selectedClientId && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Service Address <span className="text-red-500">*</span>
                </label>
                <LocationCombobox
                  clientId={selectedClientId}
                  value={selectedLocationId}
                  onValueChange={setSelectedLocationId}
                  locations={locations}
                  placeholder="Select a service address..."
                  onLocationCreated={handleLocationCreated}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Manager
                </label>
                <Select
                  value={selectedManagerId}
                  onValueChange={setSelectedManagerId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a manager..." />
                  </SelectTrigger>
                  <SelectContent>
                    {managerOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {billingAddresses.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Billing Address
                  </label>
                  <Select
                    value={selectedBillingAddressId}
                    onValueChange={setSelectedBillingAddressId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select billing address..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">No Billing Address</SelectItem>
                      {billingAddresses.map((addr) => {
                        const parts = [addr.addressLine1, addr.city, addr.state].filter(Boolean);
                        return (
                          <SelectItem key={addr.id} value={addr.id}>
                            <span className="font-medium">{addr.label || 'Untitled'}</span>
                            {addr.isDefault && !addr.managerId && <span className="text-gray-500"> (Default)</span>}
                            {parts.length > 0 && (
                              <span className="text-gray-500"> — {parts.join(', ')}</span>
                            )}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!canConfirm}
          >
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Confirm'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
